import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  Badge,
  DataTable,
  Modal,
  Textarea,
} from "../components/ui";

const TIPO_UTENZA_OPTIONS = {
  100000000: { label: "Energia elettrica", icon: "⚡" },
  100000001: { label: "Gas naturale", icon: "🔥" },
  100000002: { label: "Acqua", icon: "💧" },
  100000003: { label: "Teleriscaldamento", icon: "🌡️" },
  100000004: { label: "Telecomunicazioni", icon: "📡" },
  100000005: { label: "Rifiuti", icon: "♻️" },
  100000007: { label: "Rifiuti non speciali", icon: "♻️" },
  100000008: { label: "Rifiuti speciali", icon: "☣️" },
  100000006: { label: "Altro", icon: "📦" },
};

function ContatoriListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const [contatori, setContatori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [immobiliList, setImmobiliList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form
  const [formData, setFormData] = useState({
    codice: "",
    tipoutenza: "",
    codicecliente: "",
    indirizzo: "",
    potenzaimpegnata: "",
    attivo: true,
    dataattivazione: "",
    datadisattivazione: "",
    note: "",
    rsaId: "",
    immobileId: "",
    fornitoreId: "",
  });

  // Filtri
  const rsaIdFilter = searchParams.get("rsaId") || "";
  const tipoFilter = searchParams.get("tipoutenza") || "";
  const attivoFilter = searchParams.get("attivo") || "";

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canCreate = isAdmin || isRspp;
  const canDelete = isAdmin;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [rsaData, fornitoriData] = await Promise.all([
          apiRequest("/rsa"),
          apiRequest("/fornitori").catch(() => ({ value: [] })),
        ]);
        setRsaList(rsaData.value || []);
        setFornitoriList(fornitoriData.value || []);

        // Build query params
        const params = new URLSearchParams();
        if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
        if (tipoFilter) params.append("tipoutenza", tipoFilter);
        if (attivoFilter) params.append("attivo", attivoFilter);

        const data = await apiRequest(`/contatori?${params.toString()}`);
        setContatori(data || []);
      } catch (error) {
        console.error("Error fetching contatori:", error);
        showToast("Errore nel caricamento dei contatori", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiRequest, rsaIdFilter, tipoFilter, attivoFilter, showToast]);

  // Load immobili when RSA changes in form
  useEffect(() => {
    if (!formData.rsaId) {
      setImmobiliList([]);
      return;
    }

    const fetchImmobili = async () => {
      try {
        const data = await apiRequest(`/immobili?rsaId=${formData.rsaId}`);
        setImmobiliList(data.value || []);
      } catch {
        setImmobiliList([]);
      }
    };

    fetchImmobili();
  }, [formData.rsaId, apiRequest]);

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData({
      codice: "",
      tipoutenza: "",
      codicecliente: "",
      indirizzo: "",
      potenzaimpegnata: "",
      attivo: true,
      dataattivazione: "",
      datadisattivazione: "",
      note: "",
      rsaId: rsaIdFilter || "",
      immobileId: "",
      fornitoreId: "",
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      codice: item.codice || "",
      tipoutenza: item.tipoutenza !== null ? String(item.tipoutenza) : "",
      codicecliente: item.codicecliente || "",
      indirizzo: item.indirizzo || "",
      potenzaimpegnata: item.potenzaimpegnata ?? "",
      attivo: item.attivo !== false,
      dataattivazione: item.dataattivazione?.split("T")[0] || "",
      datadisattivazione: item.datadisattivazione?.split("T")[0] || "",
      note: item.note || "",
      rsaId: item.rsaId || "",
      immobileId: item.immobileId || "",
      fornitoreId: item.fornitoreId || "",
    });
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!formData.codice || !formData.rsaId || !formData.immobileId) {
      showToast("Codice, RSA e Immobile sono obbligatori", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        codice: formData.codice,
        tipoutenza: formData.tipoutenza ? parseInt(formData.tipoutenza) : null,
        codicecliente: formData.codicecliente || null,
        indirizzo: formData.indirizzo || null,
        potenzaimpegnata: formData.potenzaimpegnata
          ? parseFloat(formData.potenzaimpegnata)
          : null,
        attivo: formData.attivo,
        dataattivazione: formData.dataattivazione || null,
        datadisattivazione: formData.datadisattivazione || null,
        note: formData.note || null,
        rsaId: formData.rsaId,
        immobileId: formData.immobileId,
        fornitoreId: formData.fornitoreId || null,
      };

      if (editingItem) {
        await apiRequest(`/contatori/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Contatore aggiornato", "success");
      } else {
        await apiRequest("/contatori", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Contatore creato", "success");
      }

      setModalOpen(false);
      // Reload
      const params = new URLSearchParams();
      if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
      if (tipoFilter) params.append("tipoutenza", tipoFilter);
      if (attivoFilter) params.append("attivo", attivoFilter);
      const data = await apiRequest(`/contatori?${params.toString()}`);
      setContatori(data || []);
    } catch (error) {
      console.error("Error saving contatore:", error);
      showToast("Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setSaving(true);
    try {
      await apiRequest(`/contatori/${itemToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Contatore eliminato", "success");
      setDeleteModalOpen(false);
      setItemToDelete(null);
      setContatori((prev) => prev.filter((c) => c.id !== itemToDelete.id));
    } catch (error) {
      console.error("Error deleting contatore:", error);
      showToast("Errore nell'eliminazione", "error");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  const columns = [
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => {
        const tipo = TIPO_UTENZA_OPTIONS[row.tipoutenza];
        return (
          <span>
            {tipo?.icon || "📦"} {tipo?.label || "N/D"}
          </span>
        );
      },
    },
    {
      key: "codice",
      label: "Codice POD/PDR",
      render: (row) => (
        <span className="font-mono font-medium">{row.codice}</span>
      ),
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsaNome || "-",
    },
    {
      key: "immobile",
      label: "Immobile",
      render: (row) => row.immobileNome || "-",
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitoreNome || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) =>
        row.attivo ? (
          <Badge variant="success">Attivo</Badge>
        ) : (
          <Badge variant="default">Inattivo</Badge>
        ),
    },
    {
      key: "azioni",
      label: "",
      render: (row) => (
        <div className="flex gap-1">
          {canCreate && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row);
              }}
            >
              Modifica
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setItemToDelete(row);
                setDeleteModalOpen(true);
              }}
            >
              Elimina
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Registro Contatori
          </h1>
          <p className="text-gray-500 mt-1">
            POD, PDR e punti di fornitura utenze
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate("/consumi-utenze")}>
            Bollette e Consumi
          </Button>
          {canCreate && <Button onClick={openCreate}>+ Nuovo Contatore</Button>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-500">Totale contatori</p>
          <p className="text-2xl font-bold">{contatori.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Attivi</p>
          <p className="text-2xl font-bold text-green-600">
            {contatori.filter((c) => c.attivo).length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Inattivi</p>
          <p className="text-2xl font-bold text-gray-400">
            {contatori.filter((c) => !c.attivo).length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Tipi utenza</p>
          <p className="text-2xl font-bold">
            {new Set(contatori.map((c) => c.tipoutenza).filter(Boolean)).size}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSA
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={rsaIdFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo utenza
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={tipoFilter}
              onChange={(e) => handleFilterChange("tipoutenza", e.target.value)}
            >
              <option value="">Tutti i tipi</option>
              {Object.entries(TIPO_UTENZA_OPTIONS).map(([value, opt]) => (
                <option key={value} value={value}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stato
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={attivoFilter}
              onChange={(e) => handleFilterChange("attivo", e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="true">Attivi</option>
              <option value="false">Inattivi</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setSearchParams({})}
            >
              Resetta filtri
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={columns}
          data={contatori}
          loading={loading}
          emptyMessage="Nessun contatore trovato"
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Modifica Contatore" : "Nuovo Contatore"}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Codice POD/PDR *"
              name="codice"
              value={formData.codice}
              onChange={handleFormChange}
              placeholder="es. IT001E00012345"
            />
            <Select
              label="Tipo Utenza"
              name="tipoutenza"
              value={formData.tipoutenza}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Seleziona tipo" },
                ...Object.entries(TIPO_UTENZA_OPTIONS).map(([value, opt]) => ({
                  value,
                  label: `${opt.icon} ${opt.label}`,
                })),
              ]}
            />
            <Select
              label="RSA *"
              name="rsaId"
              value={formData.rsaId}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Seleziona RSA" },
                ...rsaList.map((r) => ({ value: r.id, label: r.nome })),
              ]}
            />
            <Select
              label="Immobile *"
              name="immobileId"
              value={formData.immobileId}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Seleziona immobile" },
                ...immobiliList.map((i) => ({
                  value: i.id,
                  label: i.nome,
                })),
              ]}
              disabled={!formData.rsaId}
            />
            <Select
              label="Fornitore"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Seleziona fornitore (opzionale)" },
                ...fornitoriList.map((f) => ({
                  value: f.id,
                  label: f.nome,
                })),
              ]}
            />
            <Input
              label="Codice Cliente"
              name="codicecliente"
              value={formData.codicecliente}
              onChange={handleFormChange}
              placeholder="Codice cliente presso fornitore"
            />
            <Input
              label="Indirizzo fornitura"
              name="indirizzo"
              value={formData.indirizzo}
              onChange={handleFormChange}
              placeholder="Se diverso dall'immobile"
            />
            <Input
              label="Potenza impegnata (kW)"
              name="potenzaimpegnata"
              type="number"
              step="0.01"
              value={formData.potenzaimpegnata}
              onChange={handleFormChange}
              placeholder="Solo per energia elettrica"
            />
            <Input
              label="Data attivazione"
              name="dataattivazione"
              type="date"
              value={formData.dataattivazione}
              onChange={handleFormChange}
            />
            <Input
              label="Data disattivazione"
              name="datadisattivazione"
              type="date"
              value={formData.datadisattivazione}
              onChange={handleFormChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="attivo"
              name="attivo"
              checked={formData.attivo}
              onChange={handleFormChange}
              className="rounded border-gray-300"
            />
            <label
              htmlFor="attivo"
              className="text-sm font-medium text-gray-700"
            >
              Contratto attivo
            </label>
          </div>
          <Textarea
            label="Note"
            name="note"
            value={formData.note}
            onChange={handleFormChange}
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? "Salvataggio..."
                : editingItem
                  ? "Salva Modifiche"
                  : "Crea Contatore"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Conferma Eliminazione"
      >
        <div className="space-y-4">
          <p>
            Sei sicuro di voler eliminare il contatore{" "}
            <strong>{itemToDelete?.codice}</strong>?
          </p>
          <p className="text-red-600 text-sm">
            Questa azione non può essere annullata.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? "Eliminazione..." : "Elimina"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ContatoriListPage;
